/**
 * Contrast Section - Traditional CRM vs Decision Sales OS
 * Premium interactive cards with expandable panels
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircle, CheckCircle2, Brain, Zap } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import { cn } from '@/lib/utils';

const traditionalItems = [
  { icon: Brain, text: 'You decide what matters' },
  { icon: Brain, text: 'You interpret priorities' },
  { icon: Brain, text: 'You remember to follow up' },
];

const dsosItems = [
  { icon: Zap, text: 'System decides what matters' },
  { icon: Zap, text: 'System presents clear actions' },
  { icon: Zap, text: 'System surfaces missed actions' },
];

export function ContrastSection() {
  const [selectedCard, setSelectedCard] = useState<'traditional' | 'dsos' | null>(null);

  return (
    <section className="py-20 md:py-28 px-4 bg-background border-y border-border/30">
      <div className="container mx-auto max-w-6xl">
        <ScrollReveal>
          <div className="text-center mb-12 md:mb-16">
            <p className="text-base md:text-lg text-muted-foreground leading-[var(--leading-relaxed)] mb-4 tracking-[var(--tracking-normal)]">
              Traditional CRMs require you to decide and interpret.
            </p>
            <p className="text-xl md:text-2xl lg:text-3xl font-semibold tracking-[var(--tracking-tighter)] text-foreground mb-6 leading-[var(--leading-headline)]">
              Decision Sales OS saves time by deciding what matters first.
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-[var(--leading-relaxed)] max-w-2xl mx-auto tracking-[var(--tracking-normal)]">
              The system evaluates priorities continuously and presents clear actions.
              <br />
              <span className="text-foreground font-medium tracking-[var(--tracking-tight)]">You execute. The system decides.</span>
            </p>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-[var(--gap-spacious)] max-w-5xl mx-auto">
          {/* Traditional CRM */}
          <ScrollReveal delay={0.1}>
            <motion.button
              type="button"
              onClick={() => setSelectedCard(selectedCard === 'traditional' ? null : 'traditional')}
              className={cn(
                'w-full p-6 md:p-8 dsos-card rounded-[var(--radius-card)] border bg-card/50 text-left relative overflow-hidden transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                selectedCard === 'traditional'
                  ? 'border-destructive/40 bg-card shadow-lg'
                  : 'border-border/40 hover:border-border/60'
              )}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Subtle active indicator dot */}
              {selectedCard === 'traditional' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute top-4 right-4 w-2 h-2 rounded-full bg-destructive"
                />
              )}

              <div className="flex items-center gap-3 mb-6">
                <motion.div
                  className="p-2 rounded-lg bg-destructive/10 border border-destructive/20"
                  whileHover={{ rotate: 5, scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <XCircle className="w-5 h-5 text-destructive" />
                </motion.div>
                <h3 className="text-lg md:text-xl font-semibold text-foreground">
                  Traditional CRM
                </h3>
              </div>
              <ul className="space-y-4">
                {traditionalItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.li
                      key={index}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Icon className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-sm md:text-base text-muted-foreground leading-relaxed">
                        {item.text}
                      </span>
                    </motion.li>
                  );
                })}
              </ul>

              {/* Expandable Panel */}
              <AnimatePresence>
                {selectedCard === 'traditional' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 pt-6 border-t border-border/40 overflow-hidden"
                  >
                    <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                      <p>Execution depends on manual prioritization.</p>
                      <p>When follow-ups slip, revenue risk stays invisible.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </ScrollReveal>

          {/* Decision Sales OS — single border only (no NotchedFrame) */}
          <ScrollReveal delay={0.2}>
            <motion.button
              type="button"
              onClick={() => setSelectedCard(selectedCard === 'dsos' ? null : 'dsos')}
              className={cn(
                'w-full p-6 md:p-8 dsos-card rounded-[var(--radius-card)] border bg-card relative overflow-hidden transition-all text-left',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                selectedCard === 'dsos'
                  ? 'border-primary/40 bg-gradient-to-br from-primary/[0.12] to-primary/[0.04] shadow-lg'
                  : 'border-primary/30 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] hover:border-primary/40'
              )}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                {/* Subtle glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
              
                {/* Subtle active indicator dot */}
                {selectedCard === 'dsos' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute top-4 right-4 w-2 h-2 rounded-full bg-primary z-10"
                  />
                )}

                <div className="flex items-center gap-3 mb-6 relative z-10">
                  <motion.div
                    className="p-2 rounded-lg bg-primary/20 border border-primary/30"
                    whileHover={{ rotate: -5, scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </motion.div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground">
                    Decision Sales OS
                  </h3>
                </div>
                <ul className="space-y-4 relative z-10">
                  {dsosItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <motion.li
                        key={index}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <Icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm md:text-base text-foreground/90 leading-relaxed font-medium">
                          {item.text}
                        </span>
                      </motion.li>
                    );
                  })}
                </ul>

                {/* Expandable Panel */}
                <AnimatePresence>
                  {selectedCard === 'dsos' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="mt-6 pt-6 border-t border-primary/20 overflow-hidden relative z-10"
                    >
                      <div className="space-y-2 text-sm text-foreground/80 leading-relaxed">
                        <p>The system decides what matters — then locks the next move.</p>
                        <p>Missed actions surface before revenue is lost.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
